import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type QuestionnaireCubit from "src/state/QuestionnaireCubit/QuestionnaireCubit";
import type { QuestionnaireState } from "src/state/QuestionnaireCubit/QuestionnaireState";
import {
  clearLastStep,
  loadLastStep,
  saveLastStep
} from "src/lib/journeyProgressStorage";

export const useJourneyProgressResume = (
  questionnaireId: string | undefined
): {
  onQuestionnaireData: (
    state: QuestionnaireState,
    bloc: QuestionnaireCubit
  ) => void;
  onDataSent: () => void;
  onLastStepCompleted: () => void;
} => {
  const hasResumed = useRef(false);
  const lastSavedRef = useRef<string | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const pathRef = useRef(location.pathname + location.search);
  pathRef.current = location.pathname + location.search;

  useEffect(() => {
    hasResumed.current = false;
    lastSavedRef.current = undefined;
  }, [questionnaireId]);

  const onQuestionnaireData = useCallback(
    (state: QuestionnaireState, bloc: QuestionnaireCubit) => {
      if (!questionnaireId) {
        return;
      }

      const currentRef = state.activeField?.ref;
      if (currentRef && currentRef !== lastSavedRef.current) {
        if (hasResumed.current && lastSavedRef.current !== undefined) {
          saveLastStep(questionnaireId, currentRef);
        }
        lastSavedRef.current = currentRef;
      }

      if (hasResumed.current) {
        return;
      }
      if (state.logicSteps.length === 0) {
        return;
      }

      const stored = loadLastStep(questionnaireId);
      if (!stored) {
        hasResumed.current = true;
        return;
      }

      const refInLogic = state.logicSteps.includes(stored.fieldRef);
      const refDifferent = state.activeField?.ref !== stored.fieldRef;

      if (refInLogic && refDifferent) {
        hasResumed.current = true;

        const storedIndex = state.logicSteps.indexOf(stored.fieldRef);
        const path = pathRef.current;
        for (let i = 1; i <= storedIndex; i++) {
          window.history.pushState(null, "", `${path}#${state.logicSteps[i]}`);
        }

        navigate(`${path}#${stored.fieldRef}`, { replace: true });

        bloc.handleJump({
          to: { value: stored.fieldRef, type: "field" }
        });
        return;
      }

      if (!refInLogic) {
        clearLastStep(questionnaireId);
      }
      hasResumed.current = true;
    },
    [questionnaireId]
  );

  const onDataSent = useCallback(() => {
    if (questionnaireId) {
      clearLastStep(questionnaireId);
    }
  }, [questionnaireId]);

  const onLastStepCompleted = useCallback(() => {
    if (questionnaireId) {
      clearLastStep(questionnaireId);
    }
  }, [questionnaireId]);

  return { onQuestionnaireData, onDataSent, onLastStepCompleted };
};
